import React, { useState, useEffect } from "react";
import { Users, UserCheck, UserX, Clock, MessageSquare } from "lucide-react";
import { callBackend } from "../utils/api";
import { log } from "../utils/logger";

export default function SocialManagement({
  stats,
  onAcceptFriend,
  onDenyFriend,
  onOpenChat,
  onUpdateRequests,
  onInspect,
}) {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);

  const fetchSocial = async () => {
    try {
      const friendsData = await callBackend("/social/friends");
      const requestsData = await callBackend("/social/requests/pending");
      setFriends(friendsData);
      setRequests(requestsData);
    } catch (err) {
      log("SOCIAL_MANAGEMENT", "Failed to fetch social data", err);
    }
  };

  // Update this to use the correct prop name
  const handleAcceptAction = async (id) => {
    await onAcceptFriend(id);
    fetchSocial();
  };

  // Implement the deny action
  const handleDenyAction = async (id) => {
    if (onDenyFriend) {
      await onDenyFriend(id);
      fetchSocial();
    }
  };

  useEffect(() => {
    fetchSocial();
  }, []);

  return (
    <div className="space-y-6 font-mono animate-in fade-in duration-500">
      {/* Pending Requests Section */}
      {requests.length > 0 && (
        <div className="bg-zinc-900 border-2 border-yellow-600 p-4 shadow-xl">
          <h3 className="text-yellow-500 font-black uppercase italic mb-4 flex items-center gap-2">
            <Clock size={18} /> Pending Invitations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {requests.map((req) => (
              <div
                key={req.id}
                className="bg-black border border-zinc-800 p-3 flex justify-between items-center"
              >
                <span className="text-white font-bold">{req.username}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptAction(req.id)}
                    className="bg-green-600 hover:bg-green-500 text-black px-3 py-1 text-[10px] font-black uppercase flex items-center gap-1"
                  >
                    <UserCheck size={12} /> Accept
                  </button>
                  <button
                    onClick={() => handleDenyAction(req.id)}
                    className="bg-zinc-800 hover:bg-red-600 text-zinc-400 hover:text-white px-3 py-1 text-[10px] font-black uppercase"
                  >
                    <UserX size={12} /> Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List Section */}
      <div className="bg-zinc-950 border-2 border-zinc-800 p-6">
        <h3 className="text-blue-500 font-black uppercase italic mb-6 flex items-center gap-2 border-b border-zinc-800 pb-2">
          <Users size={22} /> Your Crew
        </h3>

        <div className="grid grid-cols-1 gap-2">
          {friends.length === 0 ? (
            <p className="text-zinc-600 text-xs italic text-center py-8">
              No crew members yet. Start recruiting on the block.
            </p>
          ) : (
            friends.map((friend) => (
              <div
                key={friend.id}
                className="bg-black border border-zinc-900 p-4 flex justify-between items-center group hover:border-blue-900 transition-colors"
              >
                {/* Clickable Friend Info Area */}
                <div
                  className="flex items-center gap-4 cursor-pointer flex-1"
                  onClick={() => onInspect(friend.id)}
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      friend.is_online
                        ? "bg-green-500 shadow-[0_0_8px_green]"
                        : "bg-zinc-800"
                    }`}
                  />
                  <div>
                    <p className="text-white font-black uppercase group-hover:text-blue-400 transition-colors">
                      {friend.username}
                    </p>
                    <p className="text-[10px] text-zinc-600 uppercase">
                      Level {friend.level}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => onOpenChat(friend)}
                  className="bg-zinc-900 border border-zinc-700 hover:border-blue-500 hover:text-blue-400 p-2 text-zinc-500 transition-all ml-4"
                  title="Send Message"
                >
                  <MessageSquare size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
